import React, { useMemo, useState, useCallback } from 'react';
import * as Yup from 'yup';
import objectPath from 'object-path';
import classNames from 'classnames';

import Drawer from '@material-ui/core/Drawer';
import JourneyExperienceOverlay from './JourneyExperienceOverlay';
import { FormikTextInput } from 'src/common/form/formik/text-input';
import { JourneyForm, JourneyFormik } from '../common/JourneyFormik';
import { EMAIL_GATE_NODE_ACTIONS } from 'src/constants';
import { VideoAsset } from '../common/helpers/video_asset';
import { VideojsPlayer } from 'src/common/VideojsPlayer';
import { JourneyRequestAccessContent } from './JourneyRequestAccessContent';
import { useDeviceLayout } from 'src/utils/element/use-device-layout.hook';
import { getBrandingBackgroundColor } from 'src/utils/branding';
import JourneyLogo from 'src/images/journey-logo-new-white.svg';
import { Creator } from 'src/common/interfaces/creator.interface';
import { functionNoop } from 'src/utils/function/noop';
import JourneyPlayerPersonalizationContainer from './personalization/container';
import { Button } from 'src/common/button';
import { EmbedPlayerNdaGating } from 'src/dashboard/embed/nda-gating';

interface Props {
  journey: any;
  currentNode?: any;
  email?: string | null;
  password?: string;
  emailRequired?: boolean;
  passwordRequired?: boolean;
  accessCodeRequired?: boolean;
  emailVerificationRequired?: boolean;
  ndaSignatureRequired?: boolean;
  introVideoAsset?: VideoAsset | null;
  onSubmit?: (values: any) => Promise<any>;
  onSuccess?: (values: any) => Promise<any>;
  onCodeExpired?: () => void;
  onBypassClick?: (email: string) => void;
  onNdaSigned?: (email: string, signature: string) => void;
}

const JourneyEmailGating = ({
  journey,
  email: initialEmail,
  password,
  emailRequired,
  passwordRequired,
  accessCodeRequired,
  emailVerificationRequired,
  ndaSignatureRequired,
  introVideoAsset,
  currentNode,
  onSubmit,
  onBypassClick = functionNoop,
  onCodeExpired,
  onNdaSigned,
}: Props) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isCodeExpired, setIsCodeExpired] = useState<boolean>(false);
  const [showRequestAccess, setShowRequestAccess] = useState<boolean>(false);
  const [email, setEmail] = useState(initialEmail);
  const { isMobileLayout } = useDeviceLayout();

  const overlayContainerClass = classNames('flex flex-col w-full xs:max-w-md md:max-w-lg', {});

  const containerClass = classNames('flex flex-col space-y-4 text-bedrock-black', {});

  const formClass = classNames('relative flex flex-col space-y-2 text-left');

  const subtitleClass = classNames('text-left mt-2! text-bedrock-p text-bedrock-black', {});

  const companyLogo = useMemo(() => {
    if (currentNode) return null;

    return <JourneyPlayerPersonalizationContainer logoClasses='h-6' />;
  }, [currentNode]);

  const shouldShowEmailGateCopy = passwordRequired || emailRequired;

  const emailGateCopy = useMemo(() => {
    const copyPrefix =
      passwordRequired && emailRequired
        ? 'Enter your email address and password to'
        : passwordRequired && !emailRequired
        ? 'Enter your password to'
        : emailRequired
        ? 'Enter your email address to'
        : '';

    if (accessCodeRequired) {
      return 'Email Sent';
    }

    const actionKey = currentNode ? currentNode.content_type : 'journey';
    const actionable = EMAIL_GATE_NODE_ACTIONS[actionKey];

    return emailVerificationRequired
      ? 'Email verification required'
      : `${copyPrefix} ${actionable ? `${actionable.action} this ${actionable.obj}` : 'view the content'}`;
  }, [accessCodeRequired, currentNode, emailRequired, emailVerificationRequired, passwordRequired]);

  const formikParams = {
    initialValues: {
      email: email || '',
      password: password || '',
      access_code: '',
    },
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: (values: any) => {
      if (onSubmit) {
        return onSubmit(values)
          .then(() => {
            setIsCodeExpired(false);
            setErrorMessage(null);
            setEmail(values.email);
          })
          .catch((e: object) => {
            const errorType: string = objectPath.get(e, 'error_type', 'UnauthorizedError');
            if (errorType === 'VerificationExpiredError') {
              setIsCodeExpired(true);
              if (onCodeExpired) {
                onCodeExpired();
              }
            }
            const can_request_access: boolean = objectPath.get(e, 'data.can_request_access', false);
            if (can_request_access) {
              setEmail(values.email);
              setShowRequestAccess(true);
            } else {
              setErrorMessage(objectPath.get(e, 'message', 'Invalid credentials'));
            }
          });
      }
    },
    validationSchema: Yup.object({
      ...(emailRequired && { email: Yup.string().email('Invalid email address').required('Required') }),
      ...(passwordRequired && { password: Yup.string().required('Required') }),
      ...(accessCodeRequired && { access_code: Yup.string().required('Required') }),
    }),
  };

  const onBypassClicked = () => {
    const { email } = journey.creator as Creator;
    if (email) {
      onBypassClick(email);
    }
  };

  const renderEmailGateContent = () => {
    const orgName = journey.organization.name;
    const { hex } = getBrandingBackgroundColor(journey);
    return (
      <div className={containerClass}>
        {shouldShowEmailGateCopy && (
          <div>
            <div className='text-bedrock-h2'>{emailGateCopy}</div>
            {emailVerificationRequired && (
              <p className='text-bedrock-p mt-2'>
                {accessCodeRequired
                  ? 'Check your inbox - either copy the passcode or tap the magic link to get access to this Journey.'
                  : 'This Journey requires you to verify your email address. Enter it below to receive a passcode and magic link.'}
              </p>
            )}
          </div>
        )}
        {!!introVideoAsset && <VideojsPlayer brandingColor={hex} videoAsset={introVideoAsset} />}
        <JourneyFormik {...formikParams}>
          <JourneyForm className={formClass}>
            {emailRequired && !accessCodeRequired && (
              <FormikTextInput
                id='gate-email'
                name='email'
                type='text'
                required={true}
                placeholder='Email address'
                autoFocus={true}
              />
            )}
            {passwordRequired && !accessCodeRequired && (
              <FormikTextInput id='gate-password' required={true} name='password' type='text' placeholder='Password' />
            )}
            {accessCodeRequired && (
              <FormikTextInput
                id='gate-access-code'
                name='access_code'
                type='text'
                required={true}
                placeholder='Passcode'
              />
            )}
            {emailRequired && shouldShowEmailGateCopy && (
              <div className={subtitleClass}>Your email address will only be shared with {orgName}.</div>
            )}
            {errorMessage && <div className='text-red-500 text-sm'>{errorMessage}</div>}

            <div className='flex justify-start !mt-6'>
              <button type='submit' className='btn-bedrock-primary'>
                {isCodeExpired
                  ? 'Request New Passcode'
                  : emailVerificationRequired && !accessCodeRequired
                  ? 'Submit'
                  : 'View Journey'}
              </button>
            </div>

            {journey.can_edit && (
              <div className='flex flex-col !mt-6 space-y-6'>
                <div className='h-px bg-bedrock-gray-medium w-full rounded-[100px]' />
                <div className='flex items-center space-x-3 text-bedrock-black'>
                  <button type='button' className='btn-bedrock-secondary' onClick={onBypassClicked}>
                    Bypass
                  </button>
                  <span className='text-bedrock-p-small'>Since you created this Journey, you can bypass this</span>
                </div>
              </div>
            )}
          </JourneyForm>
        </JourneyFormik>
      </div>
    );
  };

  const renderRequestAccessContent = () => {
    return <JourneyRequestAccessContent journey={journey} initialEmail={email} />;
  };

  const renderOverlay = useCallback(
    (children) => {
      if (isMobileLayout && !currentNode) {
        return (
          <Drawer
            anchor={'bottom'}
            open={true}
            BackdropProps={{
              className: 'bedrock-backdrop-blur',
            }}
            PaperProps={{
              className: 'rounded-t-lg',
            }}
          >
            {children}
          </Drawer>
        );
      }

      return <JourneyExperienceOverlay hasRoundedCorners={Boolean(currentNode)}>{children}</JourneyExperienceOverlay>;
    },
    [isMobileLayout, currentNode]
  );

  const renderOverlayContent = () => {
    if (showRequestAccess) {
      return renderRequestAccessContent();
    }

    if (ndaSignatureRequired) {
      return (
        <EmbedPlayerNdaGating
          email={email as string}
          onSignNdaClick={(email: string, signature: string) => onNdaSigned && onNdaSigned(email, signature)}
          containerClass={containerClass} />
      )
    }

    return renderEmailGateContent();
  }

  return renderOverlay(
    <div className='flex flex-col items-center justify-center w-full h-full'>
      <div className={overlayContainerClass}>
        <div className={classNames('bg-white rounded-lg relative p-6')}>
          {companyLogo && <div className='mb-6'>{companyLogo}</div>}
          {renderOverlayContent()}
        </div>
      </div>
      {!isMobileLayout && (
        <a target='_blank' href='https://journey.io' rel='noopener noreferrer'>
          <img src={JourneyLogo} className='mt-5' alt='journey' />
        </a>
      )}
    </div>
  );
};

export default JourneyEmailGating;
